/**
 * Clerk, loaded once.
 *
 * The same instance the marketing site and the account API use, which is what
 * makes one account work for both products and is why `apps/site/src/lib/account.ts`
 * and this file load it the same way. The comment there explains the part worth
 * knowing and it is worth repeating: `@clerk/clerk-js` contains no user interface
 * at all, so `mountSignIn` on a client loaded without one throws. The interface
 * lives in `@clerk/ui`, which is either bundled — dragging React, emotion and a set
 * of wallet adapters in — or fetched from the instance's own CDN. It is fetched.
 *
 * The difference from the site is that this application *is* signed in, so Clerk
 * is loaded before the first screen rather than on one page. There is no way to be
 * useful here without knowing who somebody is.
 */
import type { Clerk } from "@clerk/clerk-js";
import { loadClerkJSScript, loadClerkUIScript } from "@clerk/shared/loadClerkJsScript";

type ClerkUIConstructor = NonNullable<NonNullable<Parameters<Clerk["load"]>[0]>["ui"]>["ClerkUI"];

declare global {
  interface Window {
    Clerk?: Clerk;
    __internal_ClerkUICtor?: ClerkUIConstructor;
  }
}

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

let client: Clerk | null = null;

export class NotConfigured extends Error {
  constructor() {
    super("VITE_CLERK_PUBLISHABLE_KEY is not set, so BroadPaper Cloud cannot sign anybody in.");
    this.name = "NotConfigured";
  }
}

/**
 * The appearance Clerk's own forms take.
 *
 * Set here rather than left default because the sign-in form is the first thing a
 * new customer sees, and a form in somebody else's colours in the middle of the
 * product reads as a redirect to a third party — which, technically, is exactly
 * what it would be.
 */
const appearance = {
  variables: {
    colorPrimary: "#d5451b",
    colorText: "#171511",
    colorTextSecondary: "#4a453c",
    colorBackground: "#fffdf9",
    colorInputBackground: "#fffdf9",
    colorDanger: "#b23a16",
    fontFamily: '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif',
    borderRadius: "4px"
  }
};

export async function loadClerk(): Promise<Clerk> {
  if (!publishableKey) throw new NotConfigured();
  if (client) return client;

  await loadClerkJSScript({ publishableKey });
  await loadClerkUIScript({ publishableKey });

  const instance = window.Clerk;
  if (!instance) throw new Error("Clerk's script loaded but left no client on the window.");
  const ClerkUI = window.__internal_ClerkUICtor;
  if (!ClerkUI) throw new Error("Clerk's interface loaded but left no components on the window.");

  await instance.load({ ui: { ClerkUI }, appearance });
  client = instance;
  return instance;
}

export function clerk(): Clerk | null {
  return client;
}

/**
 * The active session, typed off Clerk rather than by naming an exported type.
 *
 * The name of that type has moved between Clerk versions (`ActiveSessionResource`,
 * then `SignedInSessionResource`), and reading it off the client means a version
 * bump cannot break the build over a rename.
 */
export type Session = NonNullable<Clerk["session"]>;

export function session(): Session | null {
  return client?.session ?? null;
}

export function signedIn(): boolean {
  return Boolean(client?.session);
}

/**
 * Re-issues the session token with the new organisation in it.
 *
 * Needed exactly once, and the reason is worth stating: creating the workspace
 * creates a Clerk organisation *on the server*, and the token in the browser's hand
 * was minted before it existed — so the next call would arrive with no organisation
 * and be told, correctly, that there is no workspace.
 *
 * Two things make this more than one line. The client's cached user does not know
 * about an organisation it did not create, so it is reloaded first or `setActive`
 * has nothing to set. And if that still fails, the fallback is a full page load:
 * the session is then fetched from scratch and comes back with the membership Clerk
 * granted a moment ago. Reloading is inelegant and it is certain, which is the right
 * trade for the one step between signing up and using the product.
 */
export async function activateOrganisation(organisationId: string): Promise<void> {
  const instance = client;
  if (!instance) return;
  try {
    await instance.user?.reload();
    await instance.setActive({ organization: organisationId });
  } catch {
    window.location.assign("/");
    // Never resolves: the page is going away, and letting the caller continue would
    // have it render a screen against a session that is about to be replaced.
    await new Promise(() => {});
  }
}

/**
 * Opens the workspace an invitation was for, when somebody arrives from one.
 *
 * Cloud's invitations are created by the Cloud API with a redirect back here carrying
 * `?invited=<organisation>`. Clerk's own invitation screen cannot say where its link
 * goes, so it uses the instance's one fallback address — and with one sign-in shared
 * by two products, that address belongs to the SDK's account page. By the time this
 * runs the ticket has been used and the membership exists; what does not exist yet is
 * a *selected* organisation, and without one the colleague would be asked to name a
 * business.
 *
 * The parameter stays while nobody is signed in, because the sign-in screen is what
 * carries it through. Once somebody is, it is removed before anything else, so a
 * reload never repeats it; and anything that does not add up — not actually a member,
 * Clerk unreachable — falls through to the ordinary start, where Welcome lists what
 * this sign-in can open.
 */
export async function arriveFromInvitation(): Promise<void> {
  const url = new URL(window.location.href);
  const organisationId = url.searchParams.get("invited");
  const instance = client;
  if (!organisationId || !instance?.user) return;
  url.searchParams.delete("invited");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  try {
    await instance.user.reload();
    const member = instance.user.organizationMemberships.some((m) => m.organization.id === organisationId);
    if (member && instance.organization?.id !== organisationId) await instance.setActive({ organization: organisationId });
  } catch {
    // Welcome lists the workspace, so there is still one click to it.
  }
}

export function isConfigured(): boolean {
  return Boolean(publishableKey);
}
